/**
 * Global Icon Accessibility Utility
 *
 * Automatically applies aria-hidden="true" to all icons and removes
 * conflicting accessibility attributes (role="img", role="image", aria-label)
 * to treat all icons as purely decorative elements.
 */

class IconAccessibility {
  private observer: MutationObserver | null = null;

  private processedElements: WeakSet<Element> = new WeakSet();

  /**
   * Check if an element is an icon element
   */
  private isIconElement(element: Element | null | undefined): boolean {
    if (!element || !(element instanceof Element)) {
      return false;
    }

    // Check for common icon classes
    const iconClasses: string[] = [
      'base-icon',
      'io-icon',
      'fa-layers',
    ];

    // Check if element has icon-related classes
    if (iconClasses.some((className) => element.classList.contains(className))) {
      return true;
    }

    // Check for FontAwesome icons (i tags with fa- classes)
    if (element.tagName === 'I' && element.className && typeof element.className === 'string') {
      if (element.className.includes('fa-') || element.className.includes('fas ')
          || element.className.includes('far ') || element.className.includes('fal ')
          || element.className.includes('fad ') || element.className.includes('fab ')
          || element.className.includes('fa-thin ')) {
        return true;
      }
    }

    // Check for SVG icons (inside base-icon or io-icon containers)
    if (element.tagName === 'svg') {
      const parent = element.parentElement;
      if (parent && (parent.classList.contains('base-icon') || parent.classList.contains('io-icon'))) {
        return true;
      }
    }

    // Check for img tags that are likely icons (inside icon containers or small size)
    if (element.tagName === 'IMG') {
      const parent = element.parentElement;
      if (parent && (parent.classList.contains('base-icon')
                      || parent.classList.contains('io-icon')
                      || parent.classList.contains('icon')
                      || parent.classList.contains('fa-layers'))) {
        return true;
      }
    }

    return false;
  }

  /**
   * Process a single icon element
   */
  private processIconElement(element: Element): void {
    if (!element || this.processedElements.has(element)) {
      return;
    }

    // Mark as processed
    this.processedElements.add(element);

    // Add aria-hidden="true"
    element.setAttribute('aria-hidden', 'true');

    // Remove conflicting accessibility attributes
    element.removeAttribute('role');
    element.removeAttribute('aria-label');

    // For img tags, keep alt attribute but aria-hidden will override it
    // The alt is still required for images, but screen readers will ignore it
  }

  /**
   * Process all icon elements in the DOM
   */
  processAllIcons(rootElement: Document | Element = document): void {
    // Find all potential icon elements
    const selectors: string[] = [
      '.base-icon',
      '.io-icon',
      '.fa-layers',
      'i[class*="fa-"]',
      'i[class*="fas "]',
      'i[class*="far "]',
      'i[class*="fal "]',
      'i[class*="fad "]',
      'i[class*="fab "]',
      'i[class*="fa-thin "]',
    ];

    const elements = new Set<Element>();

    // Collect all matching elements
    selectors.forEach((selector) => {
      try {
        rootElement.querySelectorAll(selector).forEach((el) => {
          elements.add(el);
        });
      } catch (e) {
        // Ignore invalid selectors
      }
    });

    // Also check for SVG and IMG elements that might be icons
    rootElement.querySelectorAll('svg, img').forEach((el) => {
      if (this.isIconElement(el)) {
        elements.add(el);
      }
    });

    // Process each element
    elements.forEach((element) => {
      if (this.isIconElement(element)) {
        this.processIconElement(element);
      }
    });
  }

  /**
   * Initialize MutationObserver to handle dynamically added icons
   */
  private initObserver(): void {
    if (this.observer) {
      return;
    }

    this.observer = new MutationObserver((mutations: MutationRecord[]) => {
      mutations.forEach((mutation) => {
        // Process newly added nodes
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            // Check if the added node itself is an icon
            if (this.isIconElement(element)) {
              this.processIconElement(element);
            }

            // Check for icon elements within the added node
            this.processAllIcons(element);
          }
        });
      });
    });

    // Start observing
    if (document.body) {
      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: false,
      });
    }
  }

  /**
   * Initialize the icon accessibility utility
   */
  init(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    // Process existing icons
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.processAllIcons();
        this.initObserver();
      });
    } else {
      this.processAllIcons();
      this.initObserver();
    }
  }

  /**
   * Manually trigger icon processing (useful for Vue components)
   */
  process(): void {
    this.processAllIcons();
  }

  /**
   * Destroy the observer
   */
  destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.processedElements = new WeakSet<Element>();
  }
}

// Create singleton instance
const iconAccessibility = new IconAccessibility();

export default iconAccessibility;
